/*
 * My Shopstop V964 — canonical legacy framing + opt-in manual raster demand
 *
 * Product PDP and product Fancybox no longer use runtime optical framing.
 * normalized_v1 media carry their framing in the authored asset and all PDP /
 * lightbox presentation is handled by the canonical media window plus native
 * Fancybox/Panzoom contain/zoom behaviour. This file is now exclusively the temporary
 * legacy fallback for product-card surfaces. PDP and Fancybox have no runtime
 * normalizer lifecycle, listeners, prewarm or geometry state. Source files are never altered.
 *
 * Legacy cards now use one explicit three-state framing contract: SUBJECT when a
 * safe product envelope is available, KEEP for authored/natural composition, and
 * PENDING while subject analysis is unresolved. A short performance timer may no
 * longer convert PENDING into a permanent scale(1) result. This prevents supplier
 * canvas whitespace from becoming storefront geometry while the catalogue migration
 * to normalized_v1 is completed.
 */
(() => {
  'use strict';

  const CARD_SELECTOR = [
    '.sshl__product-media img',
    '.ssa__media img',
    '.product-card figure picture img',
    '.ss-product-card-media img',
    '.ssps__product-media img',
    '.ssps__category-media--product-fallback img.ss-product-image',
    '.ss-weekly-deals__media img',
    '.ss-themed-products__media img',
    '.ss-promo-trio__panel--product-card .ss-promo-trio__product-media img',
    '.ss-promo-trio__featured-media img.ss-product-image'
  ].join(',');

  const SELECTOR = CARD_SELECTOR;
  const NORMALIZED_PIPELINE_SELECTOR = '[data-ss-image-pipeline="normalized_v1"]';
  const IMAGE_CLASS = 'ss-product-image';
  const READY_CLASS = 'ss-image-normalizer-ready';
  const SETTLED_CLASS = 'ss-image-normalizer-settled';
  const PENDING_CLASS = 'ss-image-normalizer-pending';
  const FALLBACK_CLASS = 'ss-image-normalizer-fallback';
  // V327 pixel-analysis results remain valid; V331 combines V330 gallery safety with V328 card-loading speed.
  const CACHE_PREFIX = 'ss-image-box-v960:';
  const CACHE_VERSION = 12;
  const CACHE_TTL = 30 * 24 * 60 * 60 * 1000;
  const CACHE_INDEX_KEY = `${CACHE_PREFIX}__lru__`;
  const CACHE_INDEX_VERSION = 1;
  const CACHE_TOUCH_INTERVAL = 60 * 60 * 1000;
  const CACHE_MAINTENANCE_INTERVAL = 12 * 60 * 60 * 1000;
  const MAX_ANALYSIS_SIDE = 320;
  const hardwareConcurrency = Math.max(2, Number(navigator.hardwareConcurrency) || 4);
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);
  const compactDevice = window.matchMedia('(max-width: 760px)').matches;
  const constrainedDevice = saveData || compactDevice || hardwareConcurrency <= 4;
  const MEMORY_CACHE_LIMIT = constrainedDevice ? 96 : 192;
  const STORAGE_CACHE_LIMIT = constrainedDevice ? 160 : 320;
  const CACHE_INDEX_WRITE_DELAY = constrainedDevice ? 900 : 500;
  const QUEUE_IDLE_MIN_TIME = constrainedDevice ? 9 : 6;
  const LAYOUT_SCALE_EPSILON = 0.004;
  const LAYOUT_POSITION_EPSILON = 0.60;


  /* V918 — responsive resource selection happens before the legacy normalizer
     applies its visual scale. `sizes` therefore used to describe only the CSS
     slot, while the final raster demand could be 2–3x larger. Keep the existing
     framing target, but make source selection aware of the final transform. */
  const RASTER_QUALITY_HEADROOM = 1.15;
  const RASTER_PROMOTION_TIMEOUT_MS = 1200;
  const RASTER_PROMOTION_RECHECK_MS = 120;
  const RASTER_MIN_SCALE = 0.50;

  /* V346 keeps the exact V345 framing values, but removes the race between
     image analysis and Xtra/Swiper/catalog layout construction. Fewer parallel
     pixel jobs reduce main-thread pressure during the first interactive paint. */
  const MAX_CONCURRENT = constrainedDevice ? 2 : 3;
  const PRELOAD_DISTANCE_X = constrainedDevice ? 240 : 360;
  const PRELOAD_DISTANCE_TOP = 180;
  const PRELOAD_DISTANCE_BOTTOM = constrainedDevice ? 320 : 420;
  const OBSERVER_ROOT_MARGIN = `${PRELOAD_DISTANCE_BOTTOM + 280}px ${PRELOAD_DISTANCE_X}px`;
  // A near-viewport card should never remain visually gated for six seconds.
  // Real 2D scheduling reduces queue contention enough to use a shorter safety
  // window without changing any image framing calculation.
  const WATCHDOG_MS = constrainedDevice ? 5200 : 4200;

  /* V960 — PENDING is no longer a geometry decision. V945/V947 used a
     500/700ms visible gate that permanently froze unresolved cards at scale(1).
     On heterogeneous supplier imagery that turned network/queue timing into visual
     product size: identical products could render at radically different scales.

     V960 keeps the raster hidden until analysis produces either a safe SUBJECT frame
     or an explicit KEEP decision. Only the long safety watchdog may reveal neutral
     contain when analysis is genuinely unavailable. This removes the timing race
     without reintroducing a visible small-to-large animation. */
  /* V974 — techo de ampliacion consciente de la resolucion.
     5.0 era un techo puramente geometrico: con origenes de 500px permitia
     ampliar un producto pequeno hasta cinco veces, y la tarjeta salia grande
     pero blanda. La medicion sobre 339 packshots de Alltron da un factor
     necesario mediano de 1.3x en ventana de tarjeta y solo la cola extrema
     pasa de 2.2x. Este techo recorta esa cola sin tocar ningun encuadre que
     hoy funcione: en las paginas auditadas la escala maxima observada era
     2.016. Subir este valor solo tiene sentido cuando el catalogo tenga
     masteres de mayor resolucion. */
  const SUBJECT_MAX_SCALE = 2.2;
  const CONSERVATIVE_FRAME_PADDING = 0.055;

  /* V930 — one occupancy contract shared with the offline pipeline.
     tools/msvip/config.py uses target_fill 0.92 and elongated_target_fill 0.95
     above an aspect ratio of 2.5. Those three constants are mirrored here so a
     partially migrated grid cannot show two different product sizes.

     maxTargetFill now equals targetFill in every legacy-card context. The V345
     density boost is therefore inert for them: it could raise occupancy to 0.95
     for sparse subjects, a rule the renderer has no equivalent for, and that
     drift was the residual half of the convergence defect.

     The only remaining documented exception is `compact`/`compact360` at 0.90
     for very small transactional windows. V932 brings `promoFeatured` back to
     the shared 0.92 contract and removes its private vertical offset. */
  const ELONGATED_RATIO_THRESHOLD = 2.5;
  const ELONGATED_TARGET_FILL = 0.95;

  const CONTEXTS = {
    grid: { targetFill: 0.92, maxTargetFill: 0.92, minScale: 0.70 },
    list: { targetFill: 0.92, maxTargetFill: 0.92, minScale: 0.70 },
    mobileList: { targetFill: 0.92, maxTargetFill: 0.92, minScale: 0.70 },
    compact: { targetFill: 0.90, maxTargetFill: 0.90, minScale: 0.70 },
    // V652: Neu im Sortiment uses a 136px-high media canvas on desktop.
    // This dedicated context can enlarge or gently reduce catalogue shots so
    // visible product mass converges despite different source-file margins.
    compact360: {
      targetFill: 0.90,
      maxTargetFill: 0.90,
      minScale: 0.70,
    },
    // V716: predictive-search product media uses a square visual canvas on every
    // breakpoint (desktop 100x100, tablet 88x88, mobile 76x76). The normalizer
    // therefore stabilizes vertical, horizontal and compact products against the
    // same geometry instead of compensating for a landscape thumbnail. The short
    predictiveDesktop: {
      targetFill: 0.92,
      maxTargetFill: 0.92,
      minScale: 0.70
    },
    predictiveTablet: {
      targetFill: 0.92,
      maxTargetFill: 0.92,
      minScale: 0.70
    },
    predictiveMobile: {
      targetFill: 0.92,
      maxTargetFill: 0.92,
      minScale: 0.70
    },
    // V714: category tiles only use this context when their visual is borrowed
    // from a product. Editorial collection artwork deliberately bypasses the
    // normalizer so its authored composition is never re-framed.
    predictiveCategory: {
      targetFill: 0.92,
      maxTargetFill: 0.92,
      minScale: 0.70
    },
    railDeal: { targetFill: 0.92, maxTargetFill: 0.92, minScale: 0.70 },
    railThemed: { targetFill: 0.92, maxTargetFill: 0.92, minScale: 0.70 },
    promoPanel: { targetFill: 0.92, maxTargetFill: 0.92, minScale: 0.70 },
    // V932: featured promotional products use the same canonical optical mass
    // and mathematical centre as the rest of the 0.92 storefront contract.
    promoFeatured: { targetFill: 0.92, maxTargetFill: 0.92, minScale: 0.70 },
    categoryDesktop: { targetFill: 0.92, maxTargetFill: 0.92, minScale: 0.70 },
    categoryMobile: { targetFill: 0.92, maxTargetFill: 0.92, minScale: 0.70 }
  };

  // V957 — conservative subject-frame evidence still serves legacy cards.
  // Keep the one threshold required by the shared segmentation engine independent
  // from the retired Fancybox framing implementation.
  const AUTHORITATIVE_SUBJECT_MIN_CROP_CONFIDENCE = 0.56;


  const memoryCache = new Map();
  const storageIndexes = new WeakMap();
  const dirtyStorageIndexes = new Map();
  const appliedResults = new WeakMap();
  const appliedLayouts = new WeakMap();
  const rasterPromotionStates = new WeakMap();
  const imageViewports = new WeakMap();
  const activeJobs = new Map();
  const queue = [];
  const queued = new WeakSet();
  const queueSequence = new WeakMap();
  const processing = new WeakSet();
  const rerunAfterProcessing = new WeakSet();
  const imageGenerations = new WeakMap();
  const viewportImages = new WeakMap();
  const observedViewports = new WeakSet();
  const trackedViewports = new Set();
  const pendingNormalizerPasses = new Map();
  const pendingResizeViewports = new Set();
  let cachedStorageLayers = null;
  let running = 0;
  let resizeFrame = 0;
  /* V1160 — scroll-aware scheduling. Pixel analysis (decode + canvas +
     getImageData) is main-thread work; while the visitor is actively scrolling
     a priority request is moved to the browser's idle time (bounded by a short
     timeout) so it never competes with scroll frames and the smart header. */
  let lastScrollActivity = 0;
  const SCROLL_QUIET_MS = 140;
  const SCROLL_IDLE_TIMEOUT_MS = 260;
  let resizeViewportWidth = Math.round(window.innerWidth || document.documentElement.clientWidth || 0);
  let resizeObserverFrame = 0;
  let stableScanFrame = 0;
  let idleQueueHandle = 0;
  let storagePersistHandle = 0;
  let cacheMaintenanceHandle = 0;
  let nextQueueSequence = 1;
  let initialised = false;

  const supportsIO = 'IntersectionObserver' in window;
  const supportsRO = 'ResizeObserver' in window;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const SOURCE_FRAME = Object.freeze({ left: 0, top: 0, right: 1, bottom: 1 });

  /* V899 — one geometry owner for every normalizer context. The caller supplies
     the real subject rectangle (or the complete source frame as a safe fallback)
     and the usable window. The function is deliberately symmetric: desiredScale
     may be above or below 1, so provider whitespace can never decide visual mass. */
  function fitBoxToWindow({
    boxWidth,
    boxHeight,
    windowWidth,
    windowHeight,
    targetFill = 1,
    minScale = 0.70,
    maxScale = Infinity
  }) {
    const safeBoxWidth = Math.max(1e-6, Number(boxWidth) || 0);
    const safeBoxHeight = Math.max(1e-6, Number(boxHeight) || 0);
    const safeWindowWidth = Math.max(1e-6, Number(windowWidth) || 0);
    const safeWindowHeight = Math.max(1e-6, Number(windowHeight) || 0);
    const fill = clamp(Number(targetFill) || 1, 0.05, 1);
    const desiredScale = Math.min(
      (safeWindowWidth * fill) / safeBoxWidth,
      (safeWindowHeight * fill) / safeBoxHeight
    );
    const lower = Number.isFinite(minScale) ? minScale : 0;
    const upper = Number.isFinite(maxScale) ? Math.max(lower, maxScale) : Infinity;
    return {
      desiredScale,
      scale: clamp(desiredScale, lower, upper)
    };
  }

  function readNormalizedBounds(result, prefix = '') {
    if (!result) return null;
    const leftKey = prefix ? `${prefix}Left` : 'left';
    const topKey = prefix ? `${prefix}Top` : 'top';
    const rightKey = prefix ? `${prefix}Right` : 'right';
    const bottomKey = prefix ? `${prefix}Bottom` : 'bottom';
    const left = Number(result[leftKey]);
    const top = Number(result[topKey]);
    const right = Number(result[rightKey]);
    const bottom = Number(result[bottomKey]);
    if (![left, top, right, bottom].every(Number.isFinite)) return null;
    if (right <= left || bottom <= top) return null;
    return {
      left: clamp(left, 0, 1),
      top: clamp(top, 0, 1),
      right: clamp(right, 0, 1),
      bottom: clamp(bottom, 0, 1)
    };
  }

  function unionBounds(a, b) {
    if (!a) return b || null;
    if (!b) return a;
    return {
      left: Math.min(a.left, b.left),
      top: Math.min(a.top, b.top),
      right: Math.max(a.right, b.right),
      bottom: Math.max(a.bottom, b.bottom)
    };
  }

  function expandBounds(bounds, padding = CONSERVATIVE_FRAME_PADDING) {
    if (!bounds) return null;
    const pad = clamp(Number(padding) || 0, 0, 0.20);
    return {
      left: clamp(bounds.left - pad, 0, 1),
      top: clamp(bounds.top - pad, 0, 1),
      right: clamp(bounds.right + pad, 0, 1),
      bottom: clamp(bounds.bottom + pad, 0, 1)
    };
  }

  /* V960 — explicit SUBJECT / KEEP decision.
     Strong segmentation uses the detected product envelope directly. Catalogue
     packshots that are clearly isolated but uncertain (especially white-on-white)
     use the UNION of both independent detectors plus a 5.5% full-canvas safety
     band. That conservative envelope may include extra whitespace, but it cannot
     collapse to the entire supplier canvas merely because confidence missed one
     threshold. Authored/natural imagery remains KEEP and is never virtually cropped. */
  function getAuthoritativeSubjectFrame(result) {
    const detected = readNormalizedBounds(result);
    if (!detected) {
      return { bounds: SOURCE_FRAME, detected: false, mode: 'keep' };
    }

    const connected = readNormalizedBounds(result, 'connected');
    const cropConfidence = clamp(Number(result.cropConfidence) || 0, 0, 1);
    const componentDominance = clamp(Number(result.componentDominance) || 0, 0, 1);
    const segmentationConsensus = clamp(Number(result.segmentationConsensus) || 0, 0, 1);
    const widthCoverage = detected.right - detected.left;
    const heightCoverage = detected.bottom - detected.top;
    const areaCoverage = clamp(Number(result.areaCoverage) || widthCoverage * heightCoverage, 0, 1);
    const hasDiscardableMargin = widthCoverage < 0.985 || heightCoverage < 0.985;
    const hasLargeSupplierMargin = widthCoverage < 0.90 || heightCoverage < 0.90 || areaCoverage < 0.72;

    if (result.natural && !result.catalogue) {
      return { bounds: SOURCE_FRAME, detected: false, mode: 'keep' };
    }

    const catalogueConsensus = Boolean(
      result.catalogue &&
      segmentationConsensus >= 0.84 &&
      cropConfidence >= 0.62
    );
    const structurallyReliable = Boolean(
      !result.whiteSubjectUncertain && (
        catalogueConsensus ||
        cropConfidence >= AUTHORITATIVE_SUBJECT_MIN_CROP_CONFIDENCE ||
        (!result.natural && componentDominance >= 0.55 && segmentationConsensus >= 0.76) ||
        (componentDominance >= 0.72 && segmentationConsensus >= 0.76 && hasDiscardableMargin)
      )
    );

    if (structurallyReliable) {
      return { bounds: detected, detected: true, mode: 'subject' };
    }

    const conservativeEligible = Boolean(
      !result.natural &&
      hasLargeSupplierMargin &&
      componentDominance >= 0.34 &&
      (
        result.catalogue ||
        cropConfidence >= 0.34 ||
        segmentationConsensus >= 0.58
      )
    );

    if (conservativeEligible) {
      const conservative = expandBounds(unionBounds(detected, connected));
      if (conservative) {
        const conservativeWidth = conservative.right - conservative.left;
        const conservativeHeight = conservative.bottom - conservative.top;
        if (conservativeWidth < 0.995 || conservativeHeight < 0.995) {
          return { bounds: conservative, detected: true, mode: 'conservative' };
        }
      }
    }

    return { bounds: SOURCE_FRAME, detected: false, mode: 'keep' };
  }

  function isNormalizedPipelineImage(image) {
    return image instanceof HTMLImageElement && Boolean(image.closest(NORMALIZED_PIPELINE_SELECTOR));
  }





  function contextTypeForMediaWindow(profile) {
    switch (profile) {
      case 'home-newest': return 'compact360';
      case 'home-highlight':
      case 'collection-grid':
      case 'search-grid':
      case 'product-grid':
      case 'recommendation':
      case 'recent-viewed':
      case 'home-deal': return 'grid';
      case 'product-list': return 'list';
      case 'home-weekly': return 'railDeal';
      case 'home-themed': return 'railThemed';
      case 'promo-grid': return 'promoPanel';
      case 'promo-featured': return 'promoFeatured';
      case 'cart-main':
      case 'sticky-cart':
      case 'account-order':
      case 'upsell': return 'compact';
      case 'home-actions':
        return window.matchMedia('(max-width: 760px)').matches ? 'categoryMobile' : 'categoryDesktop';
      case 'predictive-category': return 'predictiveCategory';
      case 'predictive-product':
        return window.matchMedia('(max-width: 760px)').matches
          ? 'predictiveMobile'
          : (window.matchMedia('(max-width: 1000px)').matches ? 'predictiveTablet' : 'predictiveDesktop');
      default: return '';
    }
  }

  function getContext(image) {
    if (isNormalizedPipelineImage(image)) return null;

    const viewport = image.closest(
      '[data-ss-media-window], .sshl__product-media, .ssa__media, .product-card figure, .ss-product-card-media, .ssps__product-media, .ssps__category-media--product-fallback, .ss-weekly-deals__media, .ss-themed-products__media, .ss-promo-trio__panel--product-card .ss-promo-trio__product-media, .ss-promo-trio__featured-media'
    );
    if (!viewport) return null;

    const mediaWindowProfile = viewport.getAttribute('data-ss-media-window') || '';

    // V1001 — the AJAX side-cart is a transactional surface with a canonical
    // 140x140 contain window. It must paint the Shopify raster immediately and
    // never enter legacy optical-analysis/pending-visibility state.
    if (mediaWindowProfile === 'cart-side') return null;

    const explicitType = contextTypeForMediaWindow(mediaWindowProfile);
    if (explicitType && CONTEXTS[explicitType]) {
      return { ...CONTEXTS[explicitType], type: explicitType, viewport, mediaWindowProfile };
    }

    const width = viewport.clientWidth || 0;
    const height = viewport.clientHeight || 0;
    const weeklyDeal = viewport.classList.contains('ss-weekly-deals__media');
    const themedProduct = viewport.classList.contains('ss-themed-products__media');
    const categoryCard = viewport.classList.contains('ssa__media');
    const promoPanel = Boolean(viewport.closest('.ss-promo-trio__panel--product-card'));
    const promoFeatured = viewport.classList.contains('ss-promo-trio__featured-media');
    const explicitCompact = viewport.classList.contains('ss-product-card-media--compact');
    const predictiveSearch = viewport.classList.contains('ssps__product-media');
    const predictiveCategory = viewport.classList.contains('ssps__category-media--product-fallback');
    const compact360 = window.matchMedia('(min-width: 1001px)').matches && Boolean(viewport.closest('.sshl--compact-360'));
    const compactViewport = width > 0 && width <= 135 && height > 0 && height <= 150;
    const listStructure = Boolean(
      image.closest('.l4cl.list, .l4cl.list-compact, .product-card.list, [data-view="list"]')
    );
    const mobileList = compactViewport || (window.matchMedia('(max-width: 760px)').matches && width <= 150);
    if (compact360) {
      return { ...CONTEXTS.compact360, type: 'compact360', viewport };
    }
    if (predictiveSearch) {
      const type = window.matchMedia('(max-width: 760px)').matches
        ? 'predictiveMobile'
        : (window.matchMedia('(max-width: 1000px)').matches ? 'predictiveTablet' : 'predictiveDesktop');
      return { ...CONTEXTS[type], type, viewport };
    }
    if (predictiveCategory) {
      return { ...CONTEXTS.predictiveCategory, type: 'predictiveCategory', viewport };
    }
    const type = weeklyDeal
      ? 'railDeal'
      : (themedProduct
        ? 'railThemed'
        : (categoryCard
          ? (window.matchMedia('(max-width: 760px)').matches ? 'categoryMobile' : 'categoryDesktop')
          : (promoFeatured
            ? 'promoFeatured'
            : (promoPanel
              ? 'promoPanel'
              : (explicitCompact
                ? 'compact'
                : (mobileList ? 'mobileList' : (listStructure || width <= 220 ? 'list' : 'grid')))))));

    return { ...CONTEXTS[type], type, viewport };
  }

  function hashString(value) {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
  }

  function canonicalSource(source) {
    try {
      const url = new URL(source, window.location.href);
      ['width', 'height', 'crop', 'format', 'pad_color'].forEach((key) => {
        url.searchParams.delete(key);
      });
      return url.href;
    } catch (_error) {
      return source;
    }
  }

  function analysisSource(source) {
    try {
      const url = new URL(source, window.location.href);
      const isHttp = url.protocol === 'http:' || url.protocol === 'https:';
      const shopifyImage =
        /(^|\.)cdn\.shopify\.com$|(^|\.)shopifycdn\.net$|\.myshopify\.com$/i.test(
          url.hostname
        ) || url.origin === window.location.origin;

      if (isHttp && shopifyImage) {
        url.searchParams.set('width', String(MAX_ANALYSIS_SIDE));
        url.searchParams.delete('height');
        url.searchParams.delete('crop');
      }
      return url.href;
    } catch (_error) {
      return source;
    }
  }

  function availableStorageLayers() {
    if (cachedStorageLayers) return cachedStorageLayers;

    // V667: memory is the current-page fast path, so persisting the same payload to
    // both sessionStorage and localStorage only doubles synchronous storage work.
    // Prefer one persistent layer; fall back to sessionStorage in privacy modes.
    const layers = [];
    for (const name of ['localStorage', 'sessionStorage']) {
      try {
        const storage = window[name];
        if (!storage) continue;
        layers.push(storage);
        break;
      } catch (_error) {
        // Storage access can be denied for opaque origins or strict privacy modes.
      }
    }
    cachedStorageLayers = layers;
    return layers;
  }

  function touchMemoryCache(key, result) {
    if (memoryCache.has(key)) memoryCache.delete(key);
    memoryCache.set(key, result);

    while (memoryCache.size > MEMORY_CACHE_LIMIT) {
      const oldestKey = memoryCache.keys().next().value;
      if (typeof oldestKey === 'undefined') break;
      memoryCache.delete(oldestKey);
    }
  }

  function parseCachedValue(stored, key = null) {
    if (!stored) return null;
    try {
      const parsed = JSON.parse(stored);
      if (
        !parsed ||
        parsed.version !== CACHE_VERSION ||
        (key !== null && parsed.key !== key) ||
        typeof parsed.key !== 'string' ||
        !parsed.result ||
        (parsed.expires && parsed.expires < Date.now())
      ) return null;
      return parsed;
    } catch (_error) {
      return null;
    }
  }

  function emptyStorageIndex() {
    return {
      version: CACHE_INDEX_VERSION,
      cleanedAt: 0,
      entries: {}
    };
  }

  function persistStorageIndex(storage, index) {
    try {
      storage.setItem(CACHE_INDEX_KEY, JSON.stringify(index));
      return true;
    } catch (_error) {
      return false;
    }
  }

  function flushStorageIndexes() {
    storagePersistHandle = 0;
    const pending = Array.from(dirtyStorageIndexes.entries());
    dirtyStorageIndexes.clear();
    pending.forEach(([storage, index]) => persistStorageIndex(storage, index));
  }

  function scheduleStorageIndexPersist(storage, index, immediate = false) {
    dirtyStorageIndexes.set(storage, index);
    if (immediate) {
      if (storagePersistHandle) {
        if ('cancelIdleCallback' in window) window.cancelIdleCallback(storagePersistHandle);
        else window.clearTimeout(storagePersistHandle);
        storagePersistHandle = 0;
      }
      flushStorageIndexes();
      return;
    }
    if (storagePersistHandle) return;
    if ('requestIdleCallback' in window) {
      storagePersistHandle = window.requestIdleCallback(flushStorageIndexes, {
        timeout: CACHE_INDEX_WRITE_DELAY * 2
      });
    } else {
      storagePersistHandle = window.setTimeout(flushStorageIndexes, CACHE_INDEX_WRITE_DELAY);
    }
  }

  function rebuildStorageIndex(storage) {
    const index = emptyStorageIndex();
    const keys = [];
    const now = Date.now();

    try {
      for (let position = 0; position < storage.length; position += 1) {
        const storageKey = storage.key(position);
        if (
          storageKey &&
          storageKey !== CACHE_INDEX_KEY &&
          storageKey.startsWith(CACHE_PREFIX)
        ) keys.push(storageKey);
      }

      keys.forEach((storageKey) => {
        const cached = parseCachedValue(storage.getItem(storageKey));
        if (!cached) {
          storage.removeItem(storageKey);
          return;
        }
        const createdAt = Number(cached.createdAt) ||
          Math.max(0, Number(cached.expires) - CACHE_TTL) || now;
        index.entries[storageKey] = {
          accessedAt: Number(cached.accessedAt) || createdAt,
          expires: Number(cached.expires) || now + CACHE_TTL
        };
      });
    } catch (_error) {
      // Storage can disappear while browsing in strict privacy modes.
    }

    storageIndexes.set(storage, index);
    return index;
  }

  function getStorageIndex(storage) {
    if (storageIndexes.has(storage)) return storageIndexes.get(storage);

    try {
      const stored = storage.getItem(CACHE_INDEX_KEY);
      const parsed = stored ? JSON.parse(stored) : null;
      if (
        parsed &&
        parsed.version === CACHE_INDEX_VERSION &&
        parsed.entries &&
        typeof parsed.entries === 'object'
      ) {
        storageIndexes.set(storage, parsed);
        return parsed;
      }
    } catch (_error) {
      // Rebuild a malformed or inaccessible index from the cache entries.
    }

    return rebuildStorageIndex(storage);
  }

  function pruneStorageCache(storage, index, targetLimit = STORAGE_CACHE_LIMIT) {
    const now = Date.now();
    const entries = Object.entries(index.entries || {});

    entries.forEach(([storageKey, metadata]) => {
      let remove = !metadata || Number(metadata.expires) < now;
      if (!remove) {
        try {
          remove = storage.getItem(storageKey) === null;
        } catch (_error) {
          remove = true;
        }
      }
      if (!remove) return;
      try {
        storage.removeItem(storageKey);
      } catch (_error) {
        // Continue pruning the in-memory index even if storage is blocked.
      }
      delete index.entries[storageKey];
    });

    const remaining = Object.entries(index.entries);
    if (remaining.length > targetLimit) {
      remaining
        .sort(([, first], [, second]) =>
          (Number(first.accessedAt) || 0) - (Number(second.accessedAt) || 0)
        )
        .slice(0, remaining.length - targetLimit)
        .forEach(([storageKey]) => {
          try {
            storage.removeItem(storageKey);
          } catch (_error) {
            // Keep pruning the index even if one removal is denied.
          }
          delete index.entries[storageKey];
        });
    }

    index.cleanedAt = now;
    scheduleStorageIndexPersist(storage, index, true);
  }

  function touchStoredValue(storage, storageKey, cached, index) {
    const now = Date.now();
    const previousAccess = Number(cached.accessedAt) || 0;
    index.entries[storageKey] = {
      accessedAt: now,
      expires: Number(cached.expires) || now + CACHE_TTL
    };

    if (now - previousAccess >= CACHE_TOUCH_INTERVAL) {
      cached.accessedAt = now;
      try {
        storage.setItem(storageKey, JSON.stringify(cached));
      } catch (_error) {
        // The cached result remains usable even if its access time cannot be saved.
      }
      scheduleStorageIndexPersist(storage, index);
    }
  }

  function readSessionCache(key) {
    if (memoryCache.has(key)) {
      const result = memoryCache.get(key);
      touchMemoryCache(key, result);
      return result;
    }
    const storageKey = CACHE_PREFIX + hashString(key);

    for (const storage of availableStorageLayers()) {
      try {
        const index = getStorageIndex(storage);
        const cached = parseCachedValue(storage.getItem(storageKey), key);
        if (!cached) {
          if (index.entries[storageKey]) {
            delete index.entries[storageKey];
            storage.removeItem(storageKey);
            scheduleStorageIndexPersist(storage, index);
          }
          continue;
        }
        touchMemoryCache(key, cached.result);
        touchStoredValue(storage, storageKey, cached, index);
        return cached.result;
      } catch (_error) {
        // Storage can be blocked in private modes; continue with the next layer.
      }
    }
    return null;
  }

  function writeSessionCache(key, result) {
    touchMemoryCache(key, result);
    const storageKey = CACHE_PREFIX + hashString(key);
    const now = Date.now();
    const cached = {
      version: CACHE_VERSION,
      key,
      result,
      createdAt: now,
      accessedAt: now,
      expires: now + CACHE_TTL
    };
    const payload = JSON.stringify(cached);

    for (const storage of availableStorageLayers()) {
      try {
        const index = getStorageIndex(storage);
        let stored = false;

        try {
          storage.setItem(storageKey, payload);
          stored = true;
        } catch (_error) {
          const emergencyLimit = Math.max(24, Math.floor(STORAGE_CACHE_LIMIT * 0.75));
          pruneStorageCache(storage, index, emergencyLimit);
          try {
            storage.setItem(storageKey, payload);
            stored = true;
          } catch (_retryError) {
            // In-memory cache remains active if browser storage is unavailable/full.
          }
        }

        if (!stored) continue;
        index.entries[storageKey] = {
          accessedAt: now,
          expires: cached.expires
        };
        if (Object.keys(index.entries).length > STORAGE_CACHE_LIMIT) {
          pruneStorageCache(storage, index);
        } else {
          scheduleStorageIndexPersist(storage, index);
        }
      } catch (_error) {
        // In-memory cache remains active if browser storage is unavailable/full.
      }
    }
  }

  function maintainPersistentCache() {
    cacheMaintenanceHandle = 0;
    for (const storage of availableStorageLayers()) {
      try {
        const index = getStorageIndex(storage);
        const due = Date.now() - (Number(index.cleanedAt) || 0) >= CACHE_MAINTENANCE_INTERVAL;
        if (due || Object.keys(index.entries).length > STORAGE_CACHE_LIMIT) {
          pruneStorageCache(storage, index);
        }
      } catch (_error) {
        // Cache maintenance is optional when storage access is blocked.
      }
    }
  }

  function scheduleCacheMaintenance() {
    if (cacheMaintenanceHandle) return;
    if ('requestIdleCallback' in window) {
      cacheMaintenanceHandle = window.requestIdleCallback(maintainPersistentCache, {
        timeout: 2500
      });
    } else {
      cacheMaintenanceHandle = window.setTimeout(maintainPersistentCache, 1200);
    }
  }

  function median(values) {
    if (!values.length) return 0;
    const sorted = Array.from(values).sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2
      ? sorted[middle]
      : (sorted[middle - 1] + sorted[middle]) / 2;
  }

  function percentile(values, quantile) {
    if (!values.length) return 0;
    const sorted = Array.from(values).sort((a, b) => a - b);
    const position = clamp(
      Math.round((sorted.length - 1) * quantile),
      0,
      sorted.length - 1
    );
    return sorted[position];
  }

  function colourDistance(r, g, b, background) {
    const dr = r - background.r;
    const dg = g - background.g;
    const db = b - background.b;
    return Math.sqrt(dr * dr + dg * dg + db * db);
  }

  function luminance(r, g, b) {
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  async function decodeBitmap(source) {
    const response = await fetch(source, {
      mode: 'cors',
      credentials: 'omit',
      cache: 'force-cache'
    });
    if (!response.ok) throw new Error(`Image response ${response.status}`);
    const blob = await response.blob();

    if ('createImageBitmap' in window) return createImageBitmap(blob);

    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(blob);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(image);
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Unable to decode product image'));
      };
      image.src = objectUrl;
    });
  }

  /* V933 — independent safety estimator. Pixels sufficiently close to the
     measured border colour are flood-filled from the four source edges. Only
     regions connected to an outer edge can become background; enclosed regions
     remain subject candidates. This mirrors the offline pipeline's
     _background_connected_mask() principle without adding a second renderer. */
  function borderConnectedSubjectEnvelope(
    pixels,
    width,
    height,
    background,
    threshold,
    transparentBackground
  ) {
    const totalPixels = width * height;
    if (!totalPixels) return null;

    const closeToBackground = new Uint8Array(totalPixels);
    const connectedBackground = new Uint8Array(totalPixels);
    const queue = new Int32Array(totalPixels);
    const relaxedThreshold = clamp(Number(threshold) + 4, 12, 48);

    for (let index = 0; index < totalPixels; index += 1) {
      const offset = index * 4;
      const a = pixels[offset + 3];
      if (transparentBackground) {
        closeToBackground[index] = a <= 24 ? 1 : 0;
        continue;
      }
      if (a <= 18) {
        closeToBackground[index] = 1;
        continue;
      }
      const distance = colourDistance(
        pixels[offset],
        pixels[offset + 1],
        pixels[offset + 2],
        background
      );
      const alphaDistance = Math.abs(a - background.a);
      closeToBackground[index] = distance <= relaxedThreshold && alphaDistance <= 32 ? 1 : 0;
    }

    let head = 0;
    let tail = 0;
    const enqueue = (index) => {
      if (index < 0 || index >= totalPixels) return;
      if (!closeToBackground[index] || connectedBackground[index]) return;
      connectedBackground[index] = 1;
      queue[tail++] = index;
    };

    for (let x = 0; x < width; x += 1) {
      enqueue(x);
      enqueue((height - 1) * width + x);
    }
    for (let y = 1; y < height - 1; y += 1) {
      enqueue(y * width);
      enqueue(y * width + width - 1);
    }

    while (head < tail) {
      const current = queue[head++];
      const y = Math.floor(current / width);
      const x = current - y * width;
      for (let ny = Math.max(0, y - 1); ny <= Math.min(height - 1, y + 1); ny += 1) {
        for (let nx = Math.max(0, x - 1); nx <= Math.min(width - 1, x + 1); nx += 1) {
          if (nx === x && ny === y) continue;
          enqueue(ny * width + nx);
        }
      }
    }

    const subjectMask = new Uint8Array(totalPixels);
    for (let index = 0; index < totalPixels; index += 1) {
      const a = pixels[index * 4 + 3];
      if (a > 18 && !connectedBackground[index]) subjectMask[index] = 1;
    }

    const components = collectComponents(subjectMask, width, height);
    if (!components.length) return null;
    const largest = components[0];
    const minimumArea = Math.max(6, totalPixels * 0.00012, largest.area * 0.002);
    const retained = components.filter((component) => component.area >= minimumArea);
    if (!retained.length) return null;

    let left = width - 1;
    let right = 0;
    let top = height - 1;
    let bottom = 0;
    let subjectArea = 0;
    retained.forEach((component) => {
      left = Math.min(left, component.left);
      right = Math.max(right, component.right);
      top = Math.min(top, component.top);
      bottom = Math.max(bottom, component.bottom);
      subjectArea += component.area;
    });

    if (right <= left || bottom <= top) return null;
    const boxWidth = right - left + 1;
    const boxHeight = bottom - top + 1;
    const paddingX = Math.max(2, Math.round(boxWidth * 0.025));
    const paddingY = Math.max(2, Math.round(boxHeight * 0.025));
    left = Math.max(0, left - paddingX);
    right = Math.min(width - 1, right + paddingX);
    top = Math.max(0, top - paddingY);
    bottom = Math.min(height - 1, bottom + paddingY);

    return {
      left,
      top,
      right,
      bottom,
      subjectCoverage: clamp(subjectArea / Math.max(1, totalPixels), 0, 1),
      boxCoverage: clamp(((right - left + 1) * (bottom - top + 1)) / Math.max(1, totalPixels), 0, 1)
    };
  }

  function boundsIoU(a, b) {
    if (!a || !b) return 0;
    const intersectionLeft = Math.max(a.left, b.left);
    const intersectionTop = Math.max(a.top, b.top);
    const intersectionRight = Math.min(a.right, b.right);
    const intersectionBottom = Math.min(a.bottom, b.bottom);
    const intersectionWidth = Math.max(0, intersectionRight - intersectionLeft + 1);
    const intersectionHeight = Math.max(0, intersectionBottom - intersectionTop + 1);
    const intersection = intersectionWidth * intersectionHeight;
    const areaA = Math.max(1, (a.right - a.left + 1) * (a.bottom - a.top + 1));
    const areaB = Math.max(1, (b.right - b.left + 1) * (b.bottom - b.top + 1));
    return clamp(intersection / Math.max(1, areaA + areaB - intersection), 0, 1);
  }

  function collectComponents(mask, width, height) {
    const visited = new Uint8Array(mask.length);
    const stack = new Int32Array(mask.length);
    const components = [];

    for (let start = 0; start < mask.length; start += 1) {
      if (!mask[start] || visited[start]) continue;

      let stackSize = 0;
      stack[stackSize++] = start;
      visited[start] = 1;
      let area = 0;
      const componentPixels = [];
      let left = width;
      let right = 0;
      let top = height;
      let bottom = 0;

      while (stackSize) {
        const current = stack[--stackSize];
        const y = Math.floor(current / width);
        const x = current - y * width;
        area += 1;
        componentPixels.push(current);
        if (x < left) left = x;
        if (x > right) right = x;
        if (y < top) top = y;
        if (y > bottom) bottom = y;

        for (let ny = Math.max(0, y - 1); ny <= Math.min(height - 1, y + 1); ny += 1) {
          for (let nx = Math.max(0, x - 1); nx <= Math.min(width - 1, x + 1); nx += 1) {
            const next = ny * width + nx;
            if (!visited[next] && mask[next]) {
              visited[next] = 1;
              stack[stackSize++] = next;
            }
          }
        }
      }

      components.push({ area, left, right, top, bottom, pixels: componentPixels });
    }

    components.sort((a, b) => b.area - a.area);
    return components;
  }

  /* Ignore only a minute fraction of foreground pixels at the outer edges.
     This removes isolated JPEG/WebP edge noise without discarding narrow
     product details such as cables, straps or controller triggers. */
  function robustBounds(components, width, height, totalArea) {
    const columns = new Uint32Array(width);
    const rows = new Uint32Array(height);

    components.forEach((component) => {
      component.pixels.forEach((index) => {
        const y = Math.floor(index / width);
        const x = index - y * width;
        columns[x] += 1;
        rows[y] += 1;
      });
    });

    const trimPixels = Math.max(1, Math.floor(totalArea * 0.0008));
    const lowerBound = (counts) => {
      let accumulated = 0;
      for (let index = 0; index < counts.length; index += 1) {
        accumulated += counts[index];
        if (accumulated > trimPixels) return index;
      }
      return 0;
    };
    const upperBound = (counts) => {
      let accumulated = 0;
      for (let index = counts.length - 1; index >= 0; index -= 1) {
        accumulated += counts[index];
        if (accumulated > trimPixels) return index;
      }
      return counts.length - 1;
    };

    return {
      left: lowerBound(columns),
      right: upperBound(columns),
      top: lowerBound(rows),
      bottom: upperBound(rows)
    };
  }

  /* V958 — card-only pixel analysis. Directional margin probing and
     PDP/Fancybox containment diagnostics were retired because no remaining
     runtime consumer uses them. */
  async function analyseImage(source) {
    const bitmap = await decodeBitmap(analysisSource(source));
    const intrinsicWidth = bitmap.width || bitmap.naturalWidth;
    const intrinsicHeight = bitmap.height || bitmap.naturalHeight;

    if (!intrinsicWidth || !intrinsicHeight) {
      if (typeof bitmap.close === 'function') bitmap.close();
      return { natural: true, intrinsicRatio: 1 };
    }

    const ratio = Math.min(1, MAX_ANALYSIS_SIDE / Math.max(intrinsicWidth, intrinsicHeight));
    const width = Math.max(32, Math.round(intrinsicWidth * ratio));
    const height = Math.max(32, Math.round(intrinsicHeight * ratio));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', {
      willReadFrequently: true,
      alpha: true
    });

    if (!context) {
      if (typeof bitmap.close === 'function') bitmap.close();
      return { natural: true, intrinsicRatio: intrinsicWidth / intrinsicHeight };
    }

    context.clearRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0, width, height);
    if (typeof bitmap.close === 'function') bitmap.close();

    let pixels;
    try {
      pixels = context.getImageData(0, 0, width, height).data;
    } catch (_error) {
      return { transient: true };
    }

    const borderR = [];
    const borderG = [];
    const borderB = [];
    const borderA = [];
    const borderDepth = Math.max(1, Math.round(Math.min(width, height) * 0.012));
    const borderStep = Math.max(1, Math.floor(Math.min(width, height) / 72));

    const samplePixel = (x, y) => {
      const offset = (y * width + x) * 4;
      borderR.push(pixels[offset]);
      borderG.push(pixels[offset + 1]);
      borderB.push(pixels[offset + 2]);
      borderA.push(pixels[offset + 3]);
    };

    for (let depth = 0; depth < borderDepth; depth += 1) {
      for (let x = 0; x < width; x += borderStep) {
        samplePixel(x, depth);
        samplePixel(x, height - 1 - depth);
      }
      for (let y = borderStep; y < height - borderStep; y += borderStep) {
        samplePixel(depth, y);
        samplePixel(width - 1 - depth, y);
      }
    }

    const background = {
      r: median(borderR),
      g: median(borderG),
      b: median(borderB),
      a: median(borderA)
    };

    const borderDistances = borderR.map((r, index) =>
      colourDistance(r, borderG[index], borderB[index], background)
    );
    const borderNoise = percentile(borderDistances, 0.90);
    const borderSpread = percentile(borderDistances, 0.98);
    const transparentBackground = background.a < 42;
    const backgroundLuminance = luminance(background.r, background.g, background.b);
    const lightBackground = backgroundLuminance > 205;
    const uniformBackground = transparentBackground || borderSpread < 34;
    const threshold = clamp(borderNoise + (lightBackground ? 7 : 10), 10, 42);
    const totalPixels = width * height;
    const rawMask = new Uint8Array(totalPixels);
    const borderConnectedEnvelope = borderConnectedSubjectEnvelope(
      pixels,
      width,
      height,
      background,
      threshold,
      transparentBackground
    );

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const index = y * width + x;
        const offset = index * 4;
        const r = pixels[offset];
        const g = pixels[offset + 1];
        const b = pixels[offset + 2];
        const a = pixels[offset + 3];
        let foreground = false;

        if (transparentBackground) {
          foreground = a > 20;
        } else if (a > 18) {
          const distance = colourDistance(r, g, b, background);
          foreground = distance > threshold || Math.abs(a - background.a) > 28;

          // Four-direction local contrast keeps pale and white products detectable
          // on white backgrounds. Sparse compression noise is removed later by
          // connected-component filtering and robust outer-bound trimming.
          if (!foreground && lightBackground && distance > 2) {
            let edge = 0;
            const compareAt = (candidate) => {
              edge = Math.max(
                edge,
                Math.abs(r - pixels[candidate]) +
                  Math.abs(g - pixels[candidate + 1]) +
                  Math.abs(b - pixels[candidate + 2])
              );
            };
            if (x + 1 < width) compareAt(offset + 4);
            if (x > 0) compareAt(offset - 4);
            if (y + 1 < height) compareAt(offset + width * 4);
            if (y > 0) compareAt(offset - width * 4);
            foreground = edge > 20;
          }
        }

        if (foreground) rawMask[index] = 1;
      }
    }

    // Remove isolated compression specks while preserving narrow product details.
    const cleanMask = new Uint8Array(totalPixels);
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const index = y * width + x;
        if (!rawMask[index]) continue;
        let neighbours = 0;
        for (let ny = Math.max(0, y - 1); ny <= Math.min(height - 1, y + 1); ny += 1) {
          for (let nx = Math.max(0, x - 1); nx <= Math.min(width - 1, x + 1); nx += 1) {
            if (nx === x && ny === y) continue;
            neighbours += rawMask[ny * width + nx];
          }
        }
        if (neighbours >= 1) cleanMask[index] = 1;
      }
    }

    // One-pixel dilation reconnects fragmented light contours without changing bounds materially.
    const connectedMask = new Uint8Array(totalPixels);
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const index = y * width + x;
        if (!cleanMask[index]) continue;
        for (let ny = Math.max(0, y - 1); ny <= Math.min(height - 1, y + 1); ny += 1) {
          for (let nx = Math.max(0, x - 1); nx <= Math.min(width - 1, x + 1); nx += 1) {
            connectedMask[ny * width + nx] = 1;
          }
        }
      }
    }

    const components = collectComponents(connectedMask, width, height);
    if (!components.length) {
      return {
        natural: true,
        intrinsicRatio: intrinsicWidth / intrinsicHeight
      };
    }

    const largest = components[0];
    const minimumArea = Math.max(
      6,
      totalPixels * 0.00012,
      largest.area * 0.004
    );
    const retained = components.filter((component) => component.area >= minimumArea);

    let left = width - 1;
    let right = 0;
    let top = height - 1;
    let bottom = 0;
    let retainedArea = 0;

    retained.forEach((component) => {
      left = Math.min(left, component.left);
      right = Math.max(right, component.right);
      top = Math.min(top, component.top);
      bottom = Math.max(bottom, component.bottom);
      retainedArea += component.area;
    });

    if (retainedArea) {
      const trimmed = robustBounds(retained, width, height, retainedArea);
      left = Math.max(left, trimmed.left);
      right = Math.min(right, trimmed.right);
      top = Math.max(top, trimmed.top);
      bottom = Math.min(bottom, trimmed.bottom);
    }

    if (!retainedArea || right <= left || bottom <= top) {
      return {
        natural: true,
        intrinsicRatio: intrinsicWidth / intrinsicHeight
      };
    }

    /* V897 — the undilated retained mask is the only trustworthy foreground
       reference for margin diagnostics. V896 passed the dilated `connectedMask`,
       which still contains the small components explicitly rejected by
       `minimumArea`; that inflated `foregroundLeak` and, because cleanliness is
       a multiplicative factor, could zero out a perfectly valid trim. */
    const retainedMask = new Uint8Array(totalPixels);
    retained.forEach((component) => {
      component.pixels.forEach((index) => {
        if (cleanMask[index]) retainedMask[index] = 1;
      });
    });

    // Pre-padding subject bounds. The edge probe must start here, not at the
    // padded visual box.
    const coreBounds = { left, right, top, bottom };

    const boxWidthBeforePadding = right - left + 1;
    const boxHeightBeforePadding = bottom - top + 1;
    const paddingX = Math.max(1, Math.round(boxWidthBeforePadding * 0.018));
    const paddingY = Math.max(1, Math.round(boxHeightBeforePadding * 0.018));
    left = Math.max(0, left - paddingX);
    right = Math.min(width - 1, right + paddingX);
    top = Math.max(0, top - paddingY);
    bottom = Math.min(height - 1, bottom + paddingY);

    const boxWidth = right - left + 1;
    const boxHeight = bottom - top + 1;
    const widthCoverage = boxWidth / width;
    const heightCoverage = boxHeight / height;
    const areaCoverage = (boxWidth * boxHeight) / totalPixels;
    const visualEnvelope = { left, top, right, bottom };
    const segmentationConsensus = borderConnectedEnvelope
      ? boundsIoU(visualEnvelope, borderConnectedEnvelope)
      : 0;
    const connectedBoxCoverage = borderConnectedEnvelope
      ? borderConnectedEnvelope.boxCoverage
      : 0;
    const coverageAgreement = borderConnectedEnvelope
      ? Math.min(areaCoverage, connectedBoxCoverage) / Math.max(1e-6, Math.max(areaCoverage, connectedBoxCoverage))
      : 0;

    // Genuinely environmental imagery keeps its authored framing. Uniform
    // catalogue borders remain a useful signal, but V933 no longer treats them
    // as automatic permission to crop: pale subjects must also pass the
    // independent segmentation-consensus gate below.
    const natural =
      (!uniformBackground && widthCoverage > 0.68 && heightCoverage > 0.68) ||
      (retainedArea / totalPixels > 0.90 && areaCoverage > 0.94);

    const uniformityConfidence = clamp(1 - borderSpread / 70, 0.35, 1);
    const componentDominance = clamp(largest.area / Math.max(1, retainedArea), 0, 1);
    const componentConfidence = clamp(componentDominance, 0.35, 1);

    // V895: crop confidence is independent from the old catalogue yes/no flag.
    // A mild background gradient or a soft shadow at one edge must not discard a
    // clearly isolated product, while environmental/lifestyle imagery still needs
    // strong evidence before it may be virtually cropped. The score combines the
    // actual border stability, dominance of the principal connected component and
    // how much disposable source margin the detected product leaves behind.
    const borderCropConfidence = clamp(1 - Math.max(0, borderSpread - 18) / 58, 0, 1);
    const disposableMarginConfidence = clamp((1 - areaCoverage - 0.06) / 0.56, 0, 1);
    let cropConfidence = clamp(
      borderCropConfidence * 0.38 +
      componentDominance * 0.37 +
      disposableMarginConfidence * 0.25 +
      (uniformBackground ? 0.08 : 0),
      0,
      1
    );
    if (natural && !uniformBackground) {
      cropConfidence = Math.min(cropConfidence, 0.49);
    }

    /* V933 — white-on-white confidence cannot borrow certainty from a uniform
       border. The offline pipeline already refuses auto-normalization when a
       pale subject is not measured confidently; runtime now applies the same
       fail-closed principle. The independent border-connected estimate supplies
       the missing cross-check. */
    const whiteSubjectConfidence = clamp(
      cropConfidence * 0.45 +
      componentDominance * 0.25 +
      segmentationConsensus * 0.30,
      0,
      1
    );
    const whiteSubjectUncertain = Boolean(
      !transparentBackground &&
      lightBackground &&
      (
        !borderConnectedEnvelope ||
        segmentationConsensus < 0.78 ||
        (
          areaCoverage > 0.55 &&
          (whiteSubjectConfidence < 0.72 || coverageAgreement < 0.82)
        )
      )
    );

    /* V958 — directional margin diagnostics retired with Fancybox optical
       framing. Legacy cards consume the authoritative subject box directly. */
    return {
      natural,
      left: left / width,
      top: top / height,
      right: (right + 1) / width,
      bottom: (bottom + 1) / height,
      intrinsicRatio: intrinsicWidth / intrinsicHeight,
      catalogue: uniformBackground,
      borderSpread,
      componentDominance,
      retainedCoverage: clamp(retainedArea / Math.max(1, totalPixels), 0, 1),
      widthCoverage,
      heightCoverage,
      areaCoverage,
      cropConfidence,
      segmentationConsensus,
      connectedBoxCoverage,
      connectedSubjectCoverage: borderConnectedEnvelope ? borderConnectedEnvelope.subjectCoverage : 0,
      connectedLeft: borderConnectedEnvelope ? borderConnectedEnvelope.left / width : null,
      connectedTop: borderConnectedEnvelope ? borderConnectedEnvelope.top / height : null,
      connectedRight: borderConnectedEnvelope ? (borderConnectedEnvelope.right + 1) / width : null,
      connectedBottom: borderConnectedEnvelope ? (borderConnectedEnvelope.bottom + 1) / height : null,
      whiteSubjectConfidence,
      whiteSubjectUncertain,
      visualDensity: clamp(retainedArea / Math.max(1, boxWidth * boxHeight), 0.04, 1),
      confidence: clamp(uniformityConfidence * 0.72 + componentConfidence * 0.28, 0.35, 1)
    };
  }

  function getSource(image) {
    return image.currentSrc || image.src || '';
  }

  /* V930 — inline placeholders carry no product pixels. Previously only
     `data:image/svg` was recognised, so the transparent GIF used by AJAX
     card shells was queued for a real network analysis that could never
     succeed. Any data: URI is treated as "waiting for data", never as a
     failed image. */
  function isPlaceholderSource(source) {
    return typeof source === 'string' && source.startsWith('data:');
  }

  function srcsetWidthCandidates(image) {
    if (!(image instanceof HTMLImageElement)) return [];
    const raw = image.getAttribute('srcset') || '';
    if (!raw || !/\d+w(?:\s*,|\s*$)/.test(raw)) return [];

    const candidates = [];
    raw.split(',').forEach((part) => {
      const match = part.trim().match(/^(\S+)\s+(\d+)w$/);
      if (!match) return;
      const width = Number(match[2]);
      if (!Number.isFinite(width) || width <= 0) return;
      let href = match[1];
      try {
        href = new URL(href, window.location.href).href;
      } catch (_error) {
        // Keep the literal URL; width information remains usable.
      }
      candidates.push({ href, width });
    });
    return candidates.sort((a, b) => a.width - b.width);
  }

  function currentRasterWidth(image, candidates = srcsetWidthCandidates(image)) {
    if (!(image instanceof HTMLImageElement)) return 0;
    const source = image.currentSrc || image.src || '';
    if (!source) return 0;
    let currentHref = source;
    try {
      currentHref = new URL(source, window.location.href).href;
      const widthParam = Number(new URL(currentHref).searchParams.get('width'));
      if (Number.isFinite(widthParam) && widthParam > 0) return widthParam;
    } catch (_error) {
      // Fall through to descriptor matching.
    }
    const exact = candidates.find((candidate) => candidate.href === currentHref);
    if (exact) return exact.width;
    return Number(image.naturalWidth) || 0;
  }

  function manualPresentationScale(image) {
    if (!(image instanceof HTMLImageElement)) return 1;
    const owner = image.closest('[data-ss-product-manual-scale]');
    if (!owner) return 1;
    const value = Number(owner.dataset.ssProductManualScale);
    return Number.isFinite(value) ? clamp(value, 0.6, 1.4) : 1;
  }

  function rasterCapability(image, drawnWidth) {
    const candidates = srcsetWidthCandidates(image);
    if (!candidates.length || !Number.isFinite(drawnWidth) || drawnWidth <= 0) {
      return { candidates, maxWidth: 0, selectedWidth: 0, dpr: Math.max(1, window.devicePixelRatio || 1) };
    }
    const advertisedMaxWidth = candidates[candidates.length - 1].width;
    const sourceOwner = image.closest('[data-ss-source-width]');
    const authoritativeSourceWidth = sourceOwner ? Number(sourceOwner.dataset.ssSourceWidth) : 0;
    const maxWidth = Number.isFinite(authoritativeSourceWidth) && authoritativeSourceWidth > 0
      ? Math.min(advertisedMaxWidth, authoritativeSourceWidth)
      : advertisedMaxWidth;
    const selectedWidth = Math.min(currentRasterWidth(image, candidates), maxWidth);
    return {
      candidates,
      maxWidth,
      selectedWidth,
      dpr: Math.max(1, window.devicePixelRatio || 1)
    };
  }

  function updateRasterDiagnostics(image, selected, required, maxWidth, qualityCapped, state = '') {
    if (!(image instanceof HTMLImageElement)) return;
    const ratio = required > 0 && selected > 0 ? selected / required : 0;
    image.dataset.ssRasterSelected = selected > 0 ? String(Math.round(selected)) : 'unknown';
    image.dataset.ssRasterRequired = required > 0 ? String(Math.round(required)) : 'unknown';
    image.dataset.ssRasterMax = maxWidth > 0 ? String(Math.round(maxWidth)) : 'unknown';
    image.dataset.ssRasterRatio = ratio > 0 ? ratio.toFixed(3) : 'unknown';
    image.dataset.ssQualityCapped = qualityCapped ? 'true' : 'false';
    if (state) image.dataset.ssRasterState = state;
    else delete image.dataset.ssRasterState;
  }

  function clearRasterDiagnostics(image) {
    if (!(image instanceof HTMLImageElement)) return;
    ['ssRasterSelected', 'ssRasterRequired', 'ssRasterMax', 'ssRasterRatio', 'ssQualityCapped', 'ssRasterState'].forEach((key) => {
      delete image.dataset[key];
    });
    const state = rasterPromotionStates.get(image);
    if (state && state.timer) window.clearTimeout(state.timer);
    rasterPromotionStates.delete(image);
  }

  function ensureRasterForMeasurement(image, result, measurement) {
    const raster = measurement && measurement.raster;
    if (!raster || !raster.maxWidth || !raster.requiredPhysicalWidth) return true;

    const selected = currentRasterWidth(image, raster.candidates);
    const required = raster.requiredPhysicalWidth;
    const ratio = selected > 0 ? selected / required : 0;
    updateRasterDiagnostics(image, selected, required, raster.maxWidth, raster.qualityCapped, ratio >= 1 ? 'ready' : 'pending');
    if (selected > 0 && ratio >= 0.995) {
      const existing = rasterPromotionStates.get(image);
      if (existing && existing.timer) window.clearTimeout(existing.timer);
      rasterPromotionStates.delete(image);
      return true;
    }

    if (selected <= 0 || raster.maxWidth <= selected) return true;

    const now = Date.now();
    let state = rasterPromotionStates.get(image);
    if (!state || Math.abs(state.requiredCssWidth - raster.requiredCssWidth) > 1) {
      if (state && state.timer) window.clearTimeout(state.timer);
      state = { requiredCssWidth: raster.requiredCssWidth, startedAt: now, attempts: 0, timer: 0 };
      rasterPromotionStates.set(image, state);
    }

    if (now - state.startedAt >= RASTER_PROMOTION_TIMEOUT_MS) {
      state.forceSelectedCeiling = Math.max(1, selected);
      updateRasterDiagnostics(image, selected, required, raster.maxWidth, true, 'quality-capped');
      requestAnimationFrame(() => {
        if (image.isConnected) applyLayoutOrQueue(image, result, getSource(image));
      });
      return false;
    }

    state.attempts += 1;
    image.sizes = `${Math.ceil(raster.requiredCssWidth)}px`;
    image.dataset.ssRasterState = 'promoting';
    if (state.timer) window.clearTimeout(state.timer);
    state.timer = window.setTimeout(() => {
      state.timer = 0;
      if (!image.isConnected) return;
      image.decode().catch(() => null).finally(() => {
        if (!image.isConnected) return;
        applyLayoutOrQueue(image, result, getSource(image));
      });
    }, RASTER_PROMOTION_RECHECK_MS);
    return false;
  }

  function bumpGeneration(image) {
    const generation = (imageGenerations.get(image) || 0) + 1;
    imageGenerations.set(image, generation);
    return generation;
  }




















  function clearNormalisation(image) {
    bumpGeneration(image);
    image.classList.remove(READY_CLASS, SETTLED_CLASS, PENDING_CLASS, FALLBACK_CLASS);
    image.style.removeProperty('--ss-img-scale');
    image.style.removeProperty('--ss-img-x');
    image.style.removeProperty('--ss-img-y');
    delete image.dataset.ssNormalizerSource;
    delete image.dataset.ssNormalizerKey;
    delete image.dataset.ssNormalizerResult;
    delete image.dataset.ssNormalizerFrozen;
    delete image.dataset.ssNormalizerDeferred;
    delete image.dataset.ssNormalizerContext;
    delete image.dataset.ssNormalizerFrame;
    clearRasterDiagnostics(image);
    appliedResults.delete(image);
    appliedLayouts.delete(image);
  }

  function sourceKey(source) {
    return source ? canonicalSource(source) : '';
  }

  function storedSourceKey(image) {
    return image.dataset.ssNormalizerKey || sourceKey(image.dataset.ssNormalizerSource || '');
  }

  function rememberSource(image, source, key = sourceKey(source)) {
    if (source) image.dataset.ssNormalizerSource = source;
    if (key) image.dataset.ssNormalizerKey = key;
  }

  function revealFallback(image, source, freeze = false) {
    if (!image || !image.isConnected) return;

    /* V930 — a placeholder element that has no usable source yet is not a
       failed analysis, it is markup waiting for its data. Freezing it wrote
       `frozen` without ever writing `ssNormalizerKey`; when the real src
       arrived, refreshImageSource() stored the new key and scheduleImage()
       then matched frozen + key and returned early, so the element stayed at
       scale 1 for the rest of the page. Reached by snippets/upsell-popup.liquid
       and by any AJAX card rendered before its media is known. */
    if (!source) freeze = false;

    /* A late failure must never undo a frame that is already correct. If READY
       is already visible, FALLBACK records freeze state only and preserves the
       established geometry. */
    if (image.classList.contains(READY_CLASS) && appliedLayouts.has(image)) {
      if (freeze) image.dataset.ssNormalizerFrozen = 'true';
      return;
    }

    const currentSource = getSource(image);
    if (source && currentSource !== source) {
      const expectedKey = sourceKey(source);
      const currentKey = sourceKey(currentSource);
      if (!expectedKey || currentKey !== expectedKey) {
        clearNormalisation(image);
        scheduleImage(image);
        return;
      }
      rememberSource(image, currentSource, currentKey);
    }
    image.style.setProperty('--ss-img-scale', '1');
    image.style.setProperty('--ss-img-x', '0px');
    image.style.setProperty('--ss-img-y', '0px');
    image.dataset.ssNormalizerResult = 'fallback';
    if (freeze) image.dataset.ssNormalizerFrozen = 'true';
    image.classList.remove(PENDING_CLASS, READY_CLASS, SETTLED_CLASS);
    image.classList.add(FALLBACK_CLASS);
    appliedLayouts.set(image, { scale: 1, translateX: 0, translateY: 0, context: 'fallback' });
  }

  function getViewportContext(image) {
    const context = getContext(image);
    if (!context || !(context.viewport instanceof Element)) return null;
    return context;
  }

  function isMeasurable(image, context = getViewportContext(image)) {
    if (!image || !image.isConnected || !context || !context.viewport.isConnected) return false;
    const viewport = context.viewport;
    if (viewport.closest('[hidden]')) return false;
    const style = window.getComputedStyle(viewport);
    if (style.display === 'none' || style.contentVisibility === 'hidden') return false;
    const viewportRect = viewport.getBoundingClientRect();
    const mediaFrame = context.mediaFrame instanceof Element ? context.mediaFrame : image;
    const mediaRect = mediaFrame.getBoundingClientRect();
    return (
      viewportRect.width >= 8 &&
      viewportRect.height >= 8 &&
      mediaRect.width >= 8 &&
      mediaRect.height >= 8
    );
  }

  function releaseViewportImage(image, viewport = imageViewports.get(image)) {
    const images = viewport ? viewportImages.get(viewport) : null;
    if (images) {
      images.delete(image);
      if (!images.size) {
        viewportImages.delete(viewport);
        observedViewports.delete(viewport);
        trackedViewports.delete(viewport);
        pendingResizeViewports.delete(viewport);
        if (intersectionObserver) intersectionObserver.unobserve(viewport);
        if (resizeObserver) resizeObserver.unobserve(viewport);
      }
    }
    imageViewports.delete(image);
    appliedResults.delete(image);
    appliedLayouts.delete(image);
    clearRasterDiagnostics(image);
  }

  function registerViewport(image, context = getViewportContext(image)) {
    if (!context) return null;
    const viewport = context.viewport;
    const previousViewport = imageViewports.get(image);
    if (previousViewport && previousViewport !== viewport) {
      releaseViewportImage(image, previousViewport);
    }
    let images = viewportImages.get(viewport);
    if (!images) {
      images = new Set();
      viewportImages.set(viewport, images);
    }
    images.add(image);
    imageViewports.set(image, viewport);
    trackedViewports.add(viewport);
    if (resizeObserver && !observedViewports.has(viewport)) {
      observedViewports.add(viewport);
      resizeObserver.observe(viewport);
    }
    return viewport;
  }

  function cleanupNormalizerRoot(root) {
    if (!(root instanceof Element)) return;
    const images = [];
    if (root instanceof HTMLImageElement && root.matches(SELECTOR)) images.push(root);
    root.querySelectorAll(SELECTOR).forEach((image) => images.push(image));
    images.forEach((image) => releaseViewportImage(image));
  }

  /* Removing SETTLED in the same task as the transform write resolves
     `transition` to none for that style recalculation, so the new frame is
     painted directly. The class returns two frames later so hover stays smooth. */
  function markSettled(image) {
    image.classList.remove(SETTLED_CLASS);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (image.isConnected && image.classList.contains(READY_CLASS)) {
          image.classList.add(SETTLED_CLASS);
        }
      });
    });
  }

  function measureLayout(image, result) {
    if (!image.isConnected || !result) return null;
    const context = getViewportContext(image);
    if (!context || !isMeasurable(image, context)) return null;
    registerViewport(image, context);

    const mediaFrame = context.mediaFrame instanceof Element ? context.mediaFrame : image;
    const imageWidth = mediaFrame.clientWidth || image.clientWidth;
    const imageHeight = mediaFrame.clientHeight || image.clientHeight;
    const viewportWidth = context.viewport.clientWidth;
    const viewportHeight = context.viewport.clientHeight;
    const imageOffsetX = (viewportWidth - imageWidth) / 2;
    const imageOffsetY = (viewportHeight - imageHeight) / 2;
    if (
      imageWidth < 8 ||
      imageHeight < 8 ||
      viewportWidth < 8 ||
      viewportHeight < 8
    ) return null;

    const intrinsicRatio =
      result.intrinsicRatio || image.naturalWidth / Math.max(1, image.naturalHeight);
    if (!Number.isFinite(intrinsicRatio) || intrinsicRatio <= 0) return null;

    let scale = 1;
    let translateX = 0;
    let translateY = 0;
    let rasterMeasurement = null;

    const subjectFrame = getAuthoritativeSubjectFrame(result);

    /* V960 — KEEP is an explicit authored-composition state, not a failed crop.
       It renders the source exactly once at neutral contain. SUBJECT and
       CONSERVATIVE frames use the same canonical occupancy solver below. */
    if (subjectFrame.detected) {
      const subjectBounds = subjectFrame.bounds;
      let drawnWidth;
      let drawnHeight;
      if (imageWidth / imageHeight > intrinsicRatio) {
        drawnHeight = imageHeight;
        drawnWidth = drawnHeight * intrinsicRatio;
      } else {
        drawnWidth = imageWidth;
        drawnHeight = drawnWidth / intrinsicRatio;
      }

      const drawnLeft = (imageWidth - drawnWidth) / 2;
      const drawnTop = (imageHeight - drawnHeight) / 2;
      const boxLeft = drawnLeft + subjectBounds.left * drawnWidth;
      const boxRight = drawnLeft + subjectBounds.right * drawnWidth;
      const boxTop = drawnTop + subjectBounds.top * drawnHeight;
      const boxBottom = drawnTop + subjectBounds.bottom * drawnHeight;
      const boxWidth = Math.max(1, boxRight - boxLeft);
      const boxHeight = Math.max(1, boxBottom - boxTop);

      const density = clamp(result.visualDensity || 0.62, 0.04, 1);
      const densityBoost = clamp((0.62 - density) * 0.20, 0, context.maxTargetFill - context.targetFill);
      const subjectRatio = Math.max(boxWidth, boxHeight) / Math.max(1e-6, Math.min(boxWidth, boxHeight));
      const followsSharedContract = Math.abs(context.targetFill - 0.92) < 1e-6;
      const elongated = Boolean(
        followsSharedContract &&
        subjectRatio >= ELONGATED_RATIO_THRESHOLD
      );
      const baseFill = elongated ? ELONGATED_TARGET_FILL : context.targetFill;
      const ceilingFill = Math.max(baseFill, context.maxTargetFill);
      const targetFill = clamp(baseFill + densityBoost, baseFill, ceilingFill);

      const usableWidth = Math.max(1, viewportWidth * targetFill);
      const usableHeight = Math.max(1, viewportHeight * targetFill);
      const safeX = Math.max(0, (viewportWidth - usableWidth) / 2);
      const safeY = Math.max(0, (viewportHeight - usableHeight) / 2);
      scale = fitBoxToWindow({
        boxWidth,
        boxHeight,
        windowWidth: usableWidth,
        windowHeight: usableHeight,
        targetFill: 1,
        minScale: context.minScale,
        maxScale: SUBJECT_MAX_SCALE
      }).scale;

      /* Raster selection follows the final transform. The wider V960 card ladder
         can promote a whitespace-heavy supplier image only when its source has
         enough real pixels; data-ss-source-width remains the authoritative ceiling. */
      const raster = rasterCapability(image, drawnWidth);
      let qualityCapped = false;
      if (raster.maxWidth > 0 && drawnWidth > 0) {
        const state = rasterPromotionStates.get(image);
        const forcedSelectedCeiling = state && state.forceSelectedCeiling &&
          raster.selectedWidth > 0 && raster.selectedWidth <= state.forceSelectedCeiling + 1
          ? state.forceSelectedCeiling
          : 0;
        const rasterCeiling = forcedSelectedCeiling || raster.maxWidth;
        const manualScale = manualPresentationScale(image);
        const qualityScaleMaximum = rasterCeiling /
          Math.max(1, drawnWidth * raster.dpr * RASTER_QUALITY_HEADROOM * Math.max(1, manualScale));
        if (scale > qualityScaleMaximum) {
          scale = Math.max(RASTER_MIN_SCALE, qualityScaleMaximum);
          qualityCapped = true;
        }
        const requiredCssWidth = Math.max(1, drawnWidth * scale * manualScale * RASTER_QUALITY_HEADROOM);
        rasterMeasurement = {
          ...raster,
          requiredCssWidth,
          requiredPhysicalWidth: requiredCssWidth * raster.dpr,
          qualityCapped
        };
      }

      const boxCenterX = (boxLeft + boxRight) / 2;
      const boxCenterY = (boxTop + boxBottom) / 2;
      translateX = -scale * (boxCenterX - imageWidth / 2);
      translateY = -scale * (boxCenterY - imageHeight / 2);

      if (Number.isFinite(context.centerOffsetY)) {
        translateY += viewportHeight * context.centerOffsetY;
      }

      const finalLeft =
        imageOffsetX + imageWidth / 2 + scale * (boxLeft - imageWidth / 2) + translateX;
      const finalRight =
        imageOffsetX + imageWidth / 2 + scale * (boxRight - imageWidth / 2) + translateX;
      const finalTop =
        imageOffsetY + imageHeight / 2 + scale * (boxTop - imageHeight / 2) + translateY;
      const finalBottom =
        imageOffsetY + imageHeight / 2 + scale * (boxBottom - imageHeight / 2) + translateY;

      if (finalLeft < safeX) translateX += safeX - finalLeft;
      if (finalRight > viewportWidth - safeX) {
        translateX -= finalRight - (viewportWidth - safeX);
      }
      if (finalTop < safeY) translateY += safeY - finalTop;
      if (finalBottom > viewportHeight - safeY) {
        translateY -= finalBottom - (viewportHeight - safeY);
      }
    }

    const nextLayout = {
      scale,
      translateX,
      translateY,
      context: context.type,
      frameMode: subjectFrame.mode || (subjectFrame.detected ? 'subject' : 'keep')
    };
    return { context, layout: nextLayout, raster: rasterMeasurement };
  }

  function layoutChangeIsInsignificant(image, previousLayout, nextLayout) {
    return Boolean(
      previousLayout &&
      previousLayout.context === nextLayout.context &&
      previousLayout.frameMode === nextLayout.frameMode &&
      image.classList.contains(READY_CLASS) &&
      Math.abs(previousLayout.scale - nextLayout.scale) < LAYOUT_SCALE_EPSILON &&
      Math.abs(previousLayout.translateX - nextLayout.translateX) < LAYOUT_POSITION_EPSILON &&
      Math.abs(previousLayout.translateY - nextLayout.translateY) < LAYOUT_POSITION_EPSILON
    );
  }

  function commitLayout(image, result, measurement) {
    if (!image || !image.isConnected || !result || !measurement) return false;
    const context = measurement.context;
    if (!ensureRasterForMeasurement(image, result, measurement)) {
      image.dataset.ssNormalizerResult = 'pending-raster';
      image.classList.remove(FALLBACK_CLASS);
      image.classList.add(PENDING_CLASS);
      return false;
    }
    const nextLayout = measurement.layout;
    const previousLayout = appliedLayouts.get(image);
    const insignificantChange = layoutChangeIsInsignificant(image, previousLayout, nextLayout);

    if (!insignificantChange) {
      image.style.setProperty('--ss-img-scale', nextLayout.scale.toFixed(4));
      image.style.setProperty('--ss-img-x', `${nextLayout.translateX.toFixed(2)}px`);
      image.style.setProperty('--ss-img-y', `${nextLayout.translateY.toFixed(2)}px`);
    }

    /* Even when geometry is unchanged, keep the semantic result/cache current.
       Crucially, do not rearm card transitions or rewrite transform variables. */
    image.dataset.ssNormalizerContext = context.type;
    image.dataset.ssNormalizerFrame = nextLayout.frameMode || 'keep';
    image.dataset.ssNormalizerResult = nextLayout.frameMode === 'keep' ? 'keep' : 'ready';
    delete image.dataset.ssNormalizerFrozen;
    delete image.dataset.ssNormalizerDeferred;
    image.classList.remove(PENDING_CLASS, FALLBACK_CLASS);
    image.classList.add(READY_CLASS);
    appliedResults.set(image, result);
    appliedLayouts.set(image, nextLayout);

    /* V901 — every real transform write, in every context, lands with motion
       disabled and re-arms it two frames later. This is what makes the first
       framing and every container-resize correction instant instead of a 140ms
       animation at an unpredictable moment. No-op layouts are already filtered
       above, so the transition is never rearmed for nothing. */
    if (!insignificantChange) markSettled(image);
    return true;
  }

  function applyLayout(image, result) {
    const measurement = measureLayout(image, result);
    if (!measurement) return false;
    return commitLayout(image, result, measurement);
  }

  function applyLayoutOrQueue(image, result, source = getSource(image)) {
    if (applyLayout(image, result)) return true;
    const context = getViewportContext(image);
    if (context) {
      registerViewport(image, context);
      if (image.dataset.ssNormalizerResult !== 'pending-raster') {
        image.dataset.ssNormalizerResult = 'pending-layout';
        scheduleStableReflow(context.viewport);
      }
      image.classList.remove(FALLBACK_CLASS);
      image.classList.add(PENDING_CLASS);
      return false;
    }
    revealFallback(image, source, true);
    return false;
  }

  async function getAnalysis(source, options = null) {
    const key = canonicalSource(source);
    const cached = readSessionCache(key);
    if (cached) return cached;

    // One job per source. Directional diagnostics remain part of the cached
    // analysis schema for compatibility with existing V897+ cache entries.
    const jobKey = key;
    if (activeJobs.has(jobKey)) return activeJobs.get(jobKey);

    const job = analyseImage(source)
      .catch(() => ({ transient: true }))
      .then((result) => {
        if (!result.transient) {
          writeSessionCache(key, result);
        }
        activeJobs.delete(jobKey);
        return result;
      });

    activeJobs.set(jobKey, job);
    return job;
  }

  async function processImage(image) {
    if (!image.isConnected || !image.matches(SELECTOR) || isNormalizedPipelineImage(image)) return;
    image.classList.add(IMAGE_CLASS);

    const source = getSource(image);
    if (!source || isPlaceholderSource(source)) {
      revealFallback(image, source, true);
      return;
    }

    const key = sourceKey(source);
    const previousKey = storedSourceKey(image);
    if (previousKey && previousKey !== key) {
      clearNormalisation(image);
    } else if (image.dataset.ssNormalizerSource && image.dataset.ssNormalizerSource !== source) {
      rememberSource(image, source, key);
    }

    if (image.dataset.ssNormalizerFrozen === 'true' && storedSourceKey(image) === key) return;

    const context = getViewportContext(image);
    if (!context) {
      revealFallback(image, source, true);
      return;
    }
    registerViewport(image, context);
    rememberSource(image, source, key);

    if (!isMeasurable(image, context)) {
      image.dataset.ssNormalizerResult = 'pending-layout';
      image.classList.remove(FALLBACK_CLASS);
      image.classList.add(PENDING_CLASS);
      if (intersectionObserver) intersectionObserver.observe(context.viewport);
      return;
    }

    const generation = bumpGeneration(image);
    const cached = readSessionCache(key);
    if (cached) {
      applyLayoutOrQueue(image, cached, source);
      return;
    }

    image.dataset.ssNormalizerResult = 'pending';
    image.classList.remove(FALLBACK_CLASS);
    image.classList.add(PENDING_CLASS);

    let timedOut = false;
    const watchdogMs = WATCHDOG_MS;
    // V960: the safety watchdog is a genuine failure escape hatch, not a
    // performance budget. Until it fires the card remains PENDING and invisible.
    const freezeOnWatchdog = true;
    const watchdog = window.setTimeout(() => {
      if (
        image.isConnected &&
        sourceKey(getSource(image)) === key &&
        image.dataset.ssNormalizerResult === 'pending'
      ) {
        timedOut = true;
        image.dataset.ssNormalizerDeferred = 'true';
        revealFallback(image, source, freezeOnWatchdog);
      }
    }, watchdogMs);

    const result = await getAnalysis(source);
    window.clearTimeout(watchdog);

    if (imageGenerations.get(image) !== generation) return;

    const currentSource = getSource(image);
    const currentKey = sourceKey(currentSource);
    if (!image.isConnected || currentKey !== key) {
      clearNormalisation(image);
      scheduleImage(image);
      return;
    }
    rememberSource(image, currentSource, currentKey);

    if (result.transient) {
      /* V945 — transient analysis failure is also a one-geometry decision.
         Reveal neutral contain once and freeze this source activation instead
         of retrying after the image is already visible. A source change clears
         the freeze through clearNormalisation()/refreshImageSource(). */
      if (!timedOut) revealFallback(image, source, true);
      return;
    }

    /* V945 — every visible fallback is frozen. Late successful analysis can be
       cached for a future source/page activation but cannot resize this image. */
    if (image.dataset.ssNormalizerFrozen === 'true') return;

    applyLayoutOrQueue(image, result, source);
  }

  function viewportProximity(viewport) {
    if (!(viewport instanceof Element) || !viewport.isConnected) {
      return { near: false, distance: Number.POSITIVE_INFINITY };
    }

    const rect = viewport.getBoundingClientRect();
    const width = Math.max(1, window.innerWidth || document.documentElement.clientWidth || 1);
    const height = Math.max(1, window.innerHeight || document.documentElement.clientHeight || 1);
    const distanceX = rect.right < 0
      ? -rect.right
      : (rect.left > width ? rect.left - width : 0);
    const distanceY = rect.bottom < 0
      ? -rect.bottom
      : (rect.top > height ? rect.top - height : 0);
    const insideHorizontalBand =
      rect.right >= -PRELOAD_DISTANCE_X && rect.left <= width + PRELOAD_DISTANCE_X;
    const insideVerticalBand =
      rect.bottom >= -PRELOAD_DISTANCE_TOP && rect.top <= height + PRELOAD_DISTANCE_BOTTOM;

    return {
      near: insideHorizontalBand && insideVerticalBand,
      distance: Math.hypot(distanceX, distanceY)
    };
  }

  /* V947 — is this raster currently gated in front of a customer?
     Only card surfaces are hidden by the V945 rule, and only while they are not
     already normalized_v1. An off-screen card is not costing anyone anything. */
  function isCriticalImage(image) {
    return image.getAttribute('fetchpriority') === 'high';
  }

  function queueRank(image) {
    if (!image || !image.isConnected) return [3, Number.POSITIVE_INFINITY, Number.MAX_SAFE_INTEGER];
    if (isCriticalImage(image)) return [0, 0, queueSequence.get(image) || 0];
    const viewport = imageViewports.get(image) || getViewportContext(image)?.viewport;
    const proximity = viewportProximity(viewport);
    return [proximity.near ? 1 : 2, proximity.distance, queueSequence.get(image) || 0];
  }

  function sortQueueByProximity() {
    queue.sort((first, second) => {
      const a = queueRank(first);
      const b = queueRank(second);
      return a[0] - b[0] || a[1] - b[1] || a[2] - b[2];
    });
  }

  function shouldProcessQueuedImage(image) {
    if (isCriticalImage(image)) return true;
    const viewport = imageViewports.get(image) || getViewportContext(image)?.viewport;
    if (!viewport) return true;
    if (viewportProximity(viewport).near) return true;
    if (intersectionObserver) {
      intersectionObserver.observe(viewport);
      return false;
    }
    return true;
  }

  function runQueue(deadline = null) {
    idleQueueHandle = 0;
    if (document.hidden) return;
    let launched = 0;
    sortQueueByProximity();

    while (running < MAX_CONCURRENT && queue.length) {
      if (
        launched > 0 &&
        deadline &&
        !deadline.didTimeout &&
        deadline.timeRemaining() < QUEUE_IDLE_MIN_TIME
      ) break;

      const image = queue.shift();
      queued.delete(image);
      if (!image || !image.isConnected) {
        if (image) releaseViewportImage(image);
        continue;
      }
      if (!shouldProcessQueuedImage(image)) continue;

      processing.add(image);
      running += 1;
      launched += 1;
      processImage(image).finally(() => {
        processing.delete(image);
        running -= 1;
        if (rerunAfterProcessing.delete(image) && image.isConnected) {
          enqueue(image, true);
        }
        if (queue.length) requestQueueRun(false);
      });
    }

    if (queue.length && running < MAX_CONCURRENT) requestQueueRun(false);
  }

  function requestQueueRun(priority) {
    if (document.hidden) return;
    if (priority && performance.now() - lastScrollActivity < SCROLL_QUIET_MS) {
      if (idleQueueHandle) return;
      if ('requestIdleCallback' in window) {
        idleQueueHandle = window.requestIdleCallback(runQueue, { timeout: SCROLL_IDLE_TIMEOUT_MS });
      } else {
        idleQueueHandle = window.setTimeout(() => runQueue(), SCROLL_QUIET_MS);
      }
      return;
    }
    if (priority) {
      if (idleQueueHandle) {
        if ('cancelIdleCallback' in window) window.cancelIdleCallback(idleQueueHandle);
        else window.clearTimeout(idleQueueHandle);
        idleQueueHandle = 0;
      }
      runQueue();
      return;
    }
    if (idleQueueHandle) return;
    if ('requestIdleCallback' in window) {
      idleQueueHandle = window.requestIdleCallback(runQueue, { timeout: 700 });
    } else {
      idleQueueHandle = window.setTimeout(() => runQueue(), 24);
    }
  }

  function enqueue(image, priority = false) {
    if (!image || image.dataset.ssNormalizerFrozen === 'true') return;
    if (processing.has(image)) {
      rerunAfterProcessing.add(image);
      return;
    }
    if (queued.has(image)) {
      if (priority) requestQueueRun(true);
      return;
    }
    queued.add(image);
    queueSequence.set(image, nextQueueSequence);
    nextQueueSequence += 1;
    queue.push(image);
    requestQueueRun(priority);
  }

  function imagesForViewport(viewport) {
    return viewportImages.get(viewport) || null;
  }

  const intersectionObserver = supportsIO
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            intersectionObserver.unobserve(entry.target);
            const images = imagesForViewport(entry.target);
            if (!images || !entry.target.isConnected) {
              if (images) Array.from(images).forEach((image) => releaseViewportImage(image));
              return;
            }
            Array.from(images).forEach((image) => scheduleImage(image));
          });
        },
        { rootMargin: OBSERVER_ROOT_MARGIN, threshold: 0.01 }
      )
    : null;

  function reflowImageBatch(images) {
    const jobs = [];
    const deferred = [];
    const seen = new Set();

    images.forEach((image) => {
      if (!(image instanceof HTMLImageElement) || seen.has(image)) return;
      seen.add(image);
      if (!image.isConnected || !image.matches(SELECTOR)) return;
      if (image.dataset.ssNormalizerFrozen === 'true') return;

      const source = image.dataset.ssNormalizerSource || getSource(image);
      if (!source) return;
      const key = canonicalSource(source);
      const result = appliedResults.get(image) || readSessionCache(key);

      if (!result) {
        deferred.push(() => scheduleImage(image));
        return;
      }


      const measurement = measureLayout(image, result);
      if (measurement) jobs.push({ image, result, measurement });
    });

    /* V900: all card geometry reads complete before the first transform/class
       write. This prevents each card commit from invalidating style before the
       next card has been measured. */
    jobs.forEach(({ image, result, measurement }) => {
      commitLayout(image, result, measurement);
    });

    if (deferred.length) {
      requestAnimationFrame(() => deferred.forEach((run) => run()));
    }
  }

  function flushResizeObserver() {
    resizeObserverFrame = 0;
    const viewports = Array.from(pendingResizeViewports);
    pendingResizeViewports.clear();
    const images = [];

    viewports.forEach((viewport) => {
      const viewportSet = imagesForViewport(viewport);
      if (!viewport.isConnected || !viewportSet) {
        if (viewportSet) Array.from(viewportSet).forEach((image) => releaseViewportImage(image));
        return;
      }
      Array.from(viewportSet).forEach((image) => {
        if (!image.isConnected) {
          releaseViewportImage(image, viewport);
          return;
        }
        images.push(image);
      });
    });

    reflowImageBatch(images);
  }

  const resizeObserver = supportsRO
    ? new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          pendingResizeViewports.add(entry.target);
        });
        if (!resizeObserverFrame) {
          resizeObserverFrame = requestAnimationFrame(flushResizeObserver);
        }
      })
    : null;

  function scheduleImage(image) {
    if (!(image instanceof HTMLImageElement) || !image.matches(SELECTOR) || isNormalizedPipelineImage(image)) return;
    image.classList.add(IMAGE_CLASS);

    const source = getSource(image);
    const key = sourceKey(source);
    const previousKey = storedSourceKey(image);
    if (previousKey && key && previousKey !== key) clearNormalisation(image);
    if (image.dataset.ssNormalizerFrozen === 'true' && storedSourceKey(image) === key) return;

    const context = getViewportContext(image);
    if (!context) {
      revealFallback(image, source, true);
      return;
    }
    const viewport = registerViewport(image, context);

    if (!isMeasurable(image, context)) {
      if (intersectionObserver && viewport) intersectionObserver.observe(viewport);
      return;
    }

    if (source) {
      const cached = readSessionCache(key);
      if (cached) {
        rememberSource(image, source, key);
        applyLayoutOrQueue(image, cached, source);
        return;
      }
    }


    const proximity = viewportProximity(viewport);
    const nearViewport = isCriticalImage(image) || proximity.near;

    if (nearViewport) enqueue(image, true);
    else if (intersectionObserver) intersectionObserver.observe(viewport);
    else enqueue(image);
  }

  function manualScaleImages(root = document) {
    const images = [];
    if (root instanceof HTMLImageElement && root.matches('img.ss-product-image')) images.push(root);
    if (root && root.querySelectorAll) {
      root.querySelectorAll('[data-ss-product-manual-scale] img.ss-product-image').forEach((image) => images.push(image));
    }
    return images.filter((image, index) => images.indexOf(image) === index && manualPresentationScale(image) > 1.0001);
  }

  function syncManualRasterDemand(root = document) {
    manualScaleImages(root).forEach((image) => {
      if (!image.isConnected) return;
      const baseCssWidth = Number(image.clientWidth) || Number(image.closest('[data-ss-media-window]')?.clientWidth) || 0;
      if (baseCssWidth <= 0) return;
      const manualScale = manualPresentationScale(image);
      const requiredCssWidth = Math.ceil(baseCssWidth * manualScale);
      const currentSizes = (image.getAttribute('sizes') || '').trim();
      const nextSizes = `${requiredCssWidth}px`;
      if (currentSizes !== nextSizes) image.setAttribute('sizes', nextSizes);
    });
  }

  function scan(root = document) {
    if (root instanceof HTMLImageElement) scheduleImage(root);
    if (!root.querySelectorAll) return;
    root.querySelectorAll(SELECTOR).forEach(scheduleImage);
  }

  function reflowReadyImages(root = document) {
    if (!root.querySelectorAll) return;
    reflowImageBatch(Array.from(root.querySelectorAll(`img.${READY_CLASS}`)));
  }

  function compactScanRoots(roots) {
    if (roots.includes(document)) return [document];
    const connected = roots.filter((root) => root instanceof Element && root.isConnected);
    return connected.filter((root, index) =>
      !connected.some((other, otherIndex) =>
        otherIndex !== index && other !== root && other.contains(root)
      )
    );
  }

  function normalizerRootCovers(root, candidate) {
    if (root === document) return true;
    if (!(root instanceof Element) || !(candidate instanceof Element)) return root === candidate;
    return root === candidate || root.contains(candidate);
  }

  function flushNormalizerPasses() {
    stableScanFrame = 0;
    const requests = Array.from(pendingNormalizerPasses.entries());
    pendingNormalizerPasses.clear();
    if (!requests.length) return;

    const roots = compactScanRoots(requests.map(([root]) => root));
    requestAnimationFrame(() => {
      roots.forEach((root) => {
        if (!root || (root !== document && !root.isConnected)) return;
        const scanTargets = requests.some(([candidate, mode]) =>
          mode.scan && normalizerRootCovers(root, candidate)
        );
        const reflowReady = requests.some(([candidate, mode]) =>
          mode.reflow && normalizerRootCovers(root, candidate)
        );
        if (scanTargets) scan(root);
        if (reflowReady) reflowReadyImages(root);
      });
    });
  }

  function requestNormalizerPass(root = document, { scanTargets = false, reflowReady = true } = {}) {
    const target = root instanceof Element || root === document ? root : document;
    const previous = pendingNormalizerPasses.get(target) || { scan: false, reflow: false };
    pendingNormalizerPasses.set(target, {
      scan: previous.scan || scanTargets,
      reflow: previous.reflow || reflowReady
    });
    if (!initialised || stableScanFrame) return;
    stableScanFrame = requestAnimationFrame(() => {
      stableScanFrame = requestAnimationFrame(flushNormalizerPasses);
    });
  }

  function scheduleStableScan(root = document) {
    requestNormalizerPass(root, { scanTargets: true, reflowReady: true });
  }

  function scheduleStableReflow(root = document) {
    requestNormalizerPass(root, { scanTargets: false, reflowReady: true });
  }


  /* V958 — PDP/gallery runtime listeners retired; card targets only. */

  function refreshImageSource(image) {
    if (!(image instanceof HTMLImageElement) || !image.matches(SELECTOR) || isNormalizedPipelineImage(image)) return;
    const source = getSource(image);
    const key = sourceKey(source);
    const previousKey = storedSourceKey(image);

    if (previousKey && key && previousKey !== key) {
      clearNormalisation(image);
    } else if (source) {
      rememberSource(image, source, key);
    }
    scheduleImage(image);
  }

  document.addEventListener(
    'load',
    (event) => refreshImageSource(event.target),
    true
  );

  function containsNormalizerTarget(node) {
    if (!(node instanceof Element)) return false;
    if (node instanceof HTMLImageElement && node.matches(SELECTOR)) return !isNormalizedPipelineImage(node);
    if (!node.querySelectorAll) return false;
    return Array.from(node.querySelectorAll(SELECTOR)).some((image) => !isNormalizedPipelineImage(image));
  }

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (containsNormalizerTarget(node)) scheduleStableScan(node);
        if (node instanceof Element) requestAnimationFrame(() => syncManualRasterDemand(node));
      });
      mutation.removedNodes.forEach((node) => {
        if (containsNormalizerTarget(node)) cleanupNormalizerRoot(node);
      });
      if (
        mutation.type === 'attributes' &&
        mutation.target instanceof HTMLImageElement &&
        mutation.target.matches(SELECTOR)
      ) {
        refreshImageSource(mutation.target);
      }
    });
  });

  function initialise() {
    if (initialised) return;
    initialised = true;
    mutationObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset']
    });

    window.addEventListener(
      'scroll',
      () => {
        lastScrollActivity = performance.now();
      },
      { passive: true }
    );

    window.addEventListener(
      'resize',
      () => {
        /* V1160 — with ResizeObserver every real viewport-size change of a
           card is already observed; a height-only window resize (mobile/tablet
           toolbar collapsing mid-gesture) carries no new card geometry. */
        const nextViewportWidth = Math.round(window.innerWidth || document.documentElement.clientWidth || 0);
        const widthChanged = Math.abs(nextViewportWidth - resizeViewportWidth) > 2;
        resizeViewportWidth = nextViewportWidth;
        if (supportsRO && !widthChanged) return;
        cancelAnimationFrame(resizeFrame);
        resizeFrame = requestAnimationFrame(() => {
          if (!supportsRO) {
            scheduleStableReflow(document);
            return;
          }

          trackedViewports.forEach((viewport) => {
            if (!viewport.isConnected) {
              trackedViewports.delete(viewport);
              return;
            }
            pendingResizeViewports.add(viewport);
          });
          if (pendingResizeViewports.size && !resizeObserverFrame) {
            resizeObserverFrame = requestAnimationFrame(flushResizeObserver);
          }
          syncManualRasterDemand(document);
          if (queue.length) requestQueueRun(true);
        });
      },
      { passive: true }
    );

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) return;
      if (queue.length) requestQueueRun(true);
      scheduleStableReflow(document);
    });

    window.addEventListener('pagehide', flushStorageIndexes);

    document.addEventListener('shopify:section:load', (event) => {
      scheduleStableScan(event.target);
      requestAnimationFrame(() => syncManualRasterDemand(event.target));
    });
    window.addEventListener('ss:product-manual-scale-change', () => {
      requestAnimationFrame(() => {
        syncManualRasterDemand(document);
        scheduleStableReflow(document);
      });
    });
    document.addEventListener('shopify:section:reorder', () => scheduleStableReflow(document));
    window.addEventListener('themeVariantUpdated', () => scheduleStableReflow(document));
    window.addEventListener('themeQuickshopOpened', () => scheduleStableScan(document));
    window.addEventListener('listCollectionSlider', () => scheduleStableReflow(document));

    scheduleCacheMaintenance();
    scheduleStableScan(document);
    requestAnimationFrame(() => syncManualRasterDemand(document));
  }

  /* V958 — navigation-intent analysis prewarm retired. PDP and Fancybox no
     longer consume runtime analysis, so product-link hover/focus/pointerdown must
     not trigger an otherwise unnecessary pixel job. */

  /* V901 — repeat visits should show the finished frame from the first paint.
     initialise() deliberately waits for ss:theme-layout-ready so it never
     measures provisional slider geometry, but that leaves cards sitting in the
     neutral contain state for a second or more before the correction lands.
     This pass runs as soon as the deferred script executes and only touches
     cards that already have a cached result AND a measurable viewport: it never
     fetches, never analyses and never enqueues. If the later authoritative pass
     produces the same geometry the epsilon filter turns it into a no-op; if a
     slider changes the box, the correction is instant exactly as before. */
  function primeCachedCardLayouts() {
    let images;
    try {
      images = document.querySelectorAll(SELECTOR);
    } catch (_error) {
      return;
    }
    if (!images || !images.length) return;

    const jobs = [];
    images.forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;
      if (isNormalizedPipelineImage(image)) return;
      if (image.dataset.ssNormalizerFrozen === 'true') return;

      const source = getSource(image);
      const key = sourceKey(source);
      if (!source || !key) return;

      let cached = null;
      try {
        cached = readSessionCache(key);
      } catch (_error) {
        return;
      }
      if (!cached) return;

      const context = getViewportContext(image);
      if (!context || !isMeasurable(image, context)) return;

      image.classList.add(IMAGE_CLASS);
      rememberSource(image, source, key);
      const measurement = measureLayout(image, cached);
      if (measurement) jobs.push({ image, result: cached, measurement });
    });

    // Measure everything first, then write. Same discipline as reflowImageBatch.
    jobs.forEach(({ image, result, measurement }) => {
      commitLayout(image, result, measurement);
    });
  }

  try {
    primeCachedCardLayouts();
  } catch (_error) {
    /* A cold cache, a storage failure or an unmeasurable document must never
       prevent the normal initialisation path below from running. */
  }

  /* custom.js emits this only after custom-async.js has executed. That is the
     deterministic point at which Xtra sliders, catalog view classes and the
     product form have completed their initial structural setup. */
  if (window.__ssThemeLayoutReady || document.documentElement.classList.contains('ss-theme-layout-ready')) {
    // V663: the asset may have been loaded on demand after custom-async already
    // completed. Do not wait for a layout-ready event that has already fired.
    window.setTimeout(initialise, 0);
  } else {
    window.addEventListener('ss:theme-layout-ready', initialise, { once: true });
  }

  /* Safe fallback if an external asset is blocked: initialise after full load,
     never during the provisional DOMContentLoaded geometry used in V345. */
  if (document.readyState === 'complete') {
    window.setTimeout(initialise, 0);
  } else {
    window.addEventListener('load', () => {
      window.setTimeout(() => {
        initialise();
      }, 60);
    }, { once: true });
  }
})();
